import moment from 'moment';
import Swal from 'sweetalert2';
import { DataManager } from '../../../core/data-manager';

/**
 * Restituisce l'elenco di campi mancanti necessari per emettere l'XML SDI di una fattura.
 * Verifica dati cedente (company settings) e cessionario (customer dell'ordine).
 */
export function getMissingInvoiceFields(invoice, companySettings) {
    const missing = [];
    const customer = invoice?.idCustomerOrder?.idCustomer;
    if (!customer) {
        missing.push('Cliente non associato all\'ordine');
    } else {
        const isPrivato = parseInt(customer.category?.value) === 0;
        if (!customer.name) missing.push('Denominazione cliente');
        if (isPrivato) {
            if (!customer.taxCode) missing.push('Codice Fiscale cliente (obbligatorio per privati)');
        } else {
            if (!customer.vatNumber) missing.push('P.IVA cliente');
            if (!customer.sdi && !customer.pec) missing.push('Codice SDI o PEC cliente');
        }
        if (!customer.address) missing.push('Indirizzo cliente');
        if (!customer.cap && !customer.postalCode) missing.push('CAP cliente');
        if (!customer.city && !customer.locality) missing.push('Comune cliente');
        if (!customer.province) missing.push('Provincia cliente');
    }
    const cs = companySettings || {};
    if (!cs.PIVA) missing.push('P.IVA azienda (Impostazioni Aziendali)');
    if (!cs.denominazione) missing.push('Denominazione azienda (Impostazioni Aziendali)');
    if (!cs.indirizzo) missing.push('Indirizzo azienda (Impostazioni Aziendali)');
    if (!cs.cap) missing.push('CAP azienda (Impostazioni Aziendali)');
    if (!cs.comune) missing.push('Comune azienda (Impostazioni Aziendali)');
    if (!cs.provincia) missing.push('Provincia azienda (Impostazioni Aziendali)');
    return missing;
}

/**
 * Crea una fattura di acconto (TD02) sull'ordine, con dati inseriti manualmente.
 *
 * @param {{ totalAmount: number|string, invoiceDate?: string, description?: string }} payload
 * @param {{ id: number }} orderItem
 */
export async function createAccontoInvoice(payload, orderItem) {
    try {
        if (!orderItem || !orderItem.id) {
            Swal.fire({ title: 'Errore', text: 'Ordine non valido.', icon: 'error', confirmButtonColor: '#dc3545' });
            return { success: false };
        }
        const totalAmount = parseFloat(payload?.totalAmount || 0);
        if (!totalAmount || totalAmount <= 0) {
            Swal.fire({ title: 'Attenzione', text: 'Inserisci un importo maggiore di zero.', icon: 'warning', confirmButtonColor: '#ffc107' });
            return { success: false };
        }

        // Blocca se esiste già una fattura di saldo
        const existingSaldo = await DataManager.searchItems(
            'invoice', 1, 1,
            { field: 'id', order: 'DESC' },
            '', [],
            [
                { fieldName: 'idCustomerOrder', type: 'value', value: orderItem.id },
                { fieldName: 'invoiceType', type: 'value', value: 'saldo' }
            ]
        );
        if (existingSaldo.success === 1 && existingSaldo.body && existingSaldo.body.length > 0) {
            Swal.fire({
                title: 'Attenzione',
                text: 'È già stata emessa la fattura di saldo: non è possibile aggiungere altri acconti.',
                icon: 'warning',
                confirmButtonColor: '#ffc107'
            });
            return { success: false };
        }

        // Carica ordine per validazioni e aliquota IVA
        const orderRes = await DataManager.getItem('customer_order', orderItem.id);
        if (orderRes.success !== 1 || !orderRes.body || orderRes.body.length === 0) {
            Swal.fire({ title: 'Errore', text: 'Impossibile recuperare i dati dell\'ordine.', icon: 'error', confirmButtonColor: '#dc3545' });
            return { success: false };
        }
        const orderData = orderRes.body[0];

        // La somma di tutti gli acconti emessi + nuovo non deve eccedere il totale ordine
        const accontiRes = await DataManager.searchItems(
            'invoice', 1, 200,
            { field: 'id', order: 'DESC' },
            '', [],
            [
                { fieldName: 'idCustomerOrder', type: 'value', value: orderItem.id },
                { fieldName: 'invoiceType', type: 'value', value: 'acconto' }
            ]
        );
        const totaleAccontiEsistenti = (accontiRes.success === 1 && Array.isArray(accontiRes.body))
            ? accontiRes.body.reduce((acc, a) => acc + parseFloat(a.invoiceTotal || 0), 0)
            : 0;
        const totaleOrdine = parseFloat(orderData.totalPrice || 0);
        if ((totaleAccontiEsistenti + totalAmount) > totaleOrdine + 0.005) {
            Swal.fire({
                title: 'Attenzione',
                html: `<p>L'importo eccede il residuo dell'ordine.</p>
                    <p>Totale ordine: <b>${totaleOrdine.toFixed(2)} €</b></p>
                    <p>Acconti già emessi: <b>${totaleAccontiEsistenti.toFixed(2)} €</b></p>
                    <p>Residuo disponibile: <b>${(totaleOrdine - totaleAccontiEsistenti).toFixed(2)} €</b></p>`,
                icon: 'warning',
                confirmButtonColor: '#ffc107'
            });
            return { success: false };
        }

        // Scorporo IVA usando l'aliquota dell'ordine
        const aliquotaOrdine = parseFloat(orderData.idVatCondition?.percentage || 22);
        const imponibileAcconto = aliquotaOrdine > 0 ? (totalAmount / (1 + aliquotaOrdine / 100)) : totalAmount;
        const ivaAcconto = totalAmount - imponibileAcconto;
        const invoiceDate = payload?.invoiceDate || moment().format('YYYY-MM-DD');
        const description = payload?.description && payload.description.trim().length > 0
            ? payload.description.trim()
            : `Acconto su ordine ${orderData.internalCode || orderItem.id}`;

        const invoiceData = {
            idCustomerOrder: { id: orderItem.id },
            idVatCondition: orderData.idVatCondition || null,
            invoiceType: { value: 'acconto', label: 'acconto' },
            invoiceDate: invoiceDate,
            status: { value: '0_PENDING', label: '0_PENDING' },
            invoiceImponibile: imponibileAcconto.toFixed(2),
            invoiceIva: ivaAcconto.toFixed(2),
            invoiceTotal: totalAmount.toFixed(2),
            rows: [{
                name: 'Acconto',
                description: description,
                quantity: 1,
                listPrice: imponibileAcconto.toFixed(2),
                discount: '0',
                vat: aliquotaOrdine,
                price: imponibileAcconto.toFixed(2),
                vatPrice: ivaAcconto.toFixed(2),
                totalPrice: totalAmount.toFixed(2),
                notes: ''
            }]
        };

        const response = await DataManager.insertItem('invoice', invoiceData);
        if (response.success !== 1) {
            Swal.fire({ title: 'Errore', text: response.message || 'Impossibile creare la fattura di acconto.', icon: 'error', confirmButtonColor: '#dc3545' });
            return { success: false };
        }

        Swal.fire({ title: 'Completato', text: 'Fattura di acconto creata con successo.', icon: 'success', confirmButtonColor: '#198754' });
        return { success: true, invoice: response.body };
    } catch (error) {
        Swal.fire({ title: 'Errore', text: error.message || 'Errore durante la creazione della fattura di acconto.', icon: 'error', confirmButtonColor: '#dc3545' });
        return { success: false };
    }
}
